/* eslint-disable max-lines */
import { useZoneOccupancy } from '@hakimo-ui/hakimo/data-access';
import {
  PresenceLiveness,
  PresenceRule,
  PresenceRuleType,
  PresenceZone,
} from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { eventTracker } from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  InputField,
  Modal,
  Toggle,
} from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  countHint,
  defaultRule,
  describeRule,
  livenessHint,
  RULE_TYPE_OPTIONS,
  RuleTypeOption,
  ruleTargetsZone,
  usesCount,
  usesMinutes,
  usesWindow,
  validateRule,
  withType,
  zoneLabel,
} from './ruleSentence';

interface ZoneOption {
  id: string;
  name: string;
  zoneName: string;
}

interface Props {
  cameraId: string;
  zones: PresenceZone[];
  rule?: PresenceRule;
  liveness?: PresenceLiveness;
  isSaving: boolean;
  error?: string | null;
  onSave: (rule: PresenceRule) => void;
  onClose: () => void;
}

export function AddEditPresenceRule(props: Props) {
  const { cameraId, zones, rule, liveness, isSaving, error, onSave, onClose } =
    props;
  const isEditing = !!rule;

  // Named zones first; the whole camera last, as "anywhere in view".
  const zoneOptions: ZoneOption[] = useMemo(
    () =>
      [...zones]
        .sort((a, b) => Number(a.isDefault) - Number(b.isDefault))
        .map((z) => ({
          id: z.id,
          name: z.isDefault
            ? `${zoneLabel(z.name)} (anywhere in view)`
            : z.name,
          zoneName: z.name,
        })),
    [zones]
  );

  const [formValue, setFormValue] = useState<PresenceRule>(() => {
    if (!rule) {
      return defaultRule(
        'max_occupancy',
        zoneOptions[0]
          ? { id: zoneOptions[0].id, name: zoneOptions[0].zoneName }
          : { id: null, name: '' },
        cameraId
      );
    }
    // A rule written before ids carries only a zone name, and the camera save
    // refuses that. Resolve it here so opening the rule is enough to fix it.
    const match = zoneOptions.find((z) =>
      ruleTargetsZone(rule, { id: z.id, name: z.zoneName })
    );
    return rule.zoneId || !match ? rule : { ...rule, zoneId: match.id };
  });

  const typeOption = RULE_TYPE_OPTIONS.find((o) => o.id === formValue.type);
  const zoneOption = zoneOptions.find((z) =>
    ruleTargetsZone(formValue, { id: z.id, name: z.zoneName })
  );

  // The peak the limit is judged against is this zone's own, over the same
  // day the occupancy card draws -- the last count seen in some other zone
  // says nothing about what to allow here.
  const since = useMemo(
    () => dayjs().subtract(24, 'hour').startOf('hour').toISOString(),
    []
  );
  const occupancy = useZoneOccupancy(
    formValue.zoneId ?? '',
    { from: since, bucket: '15m' },
    !!formValue.zoneId && formValue.type === 'max_occupancy'
  );
  const peakToday = useMemo(() => {
    const observed = (occupancy.data?.items ?? []).filter(
      (b) => b.observedSeconds > 0
    );
    return observed.length ? Math.max(...observed.map((b) => b.max)) : null;
  }, [occupancy.data]);

  const problem = validateRule(formValue);
  const minutesCaution = livenessHint(liveness, formValue);
  const countCaution = countHint(formValue, peakToday);

  const numberUpdater =
    (name: 'count' | 'thresholdMinutes' | 'windowMinutes') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const value = raw === '' ? 0 : Number(raw);
      setFormValue((prev) => ({ ...prev, [name]: value }));
    };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={() => onSave(formValue)}
        disabled={!!problem || isSaving}
        loading={isSaving}
        onSideEffect={eventTracker(
          isEditing ? 'submit_update_presence_rule' : 'submit_add_presence_rule'
        )}
      >
        {isEditing ? 'Save' : 'Add rule'}
      </Button>
      <Button
        onClick={onClose}
        onSideEffect={eventTracker(
          isEditing ? 'cancel_update_presence_rule' : 'cancel_add_presence_rule'
        )}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      title={isEditing ? 'Edit presence rule' : 'Add presence rule'}
      open
      onClose={onClose}
      footer={actions}
    >
      {error && (
        <div className="mt-4 px-8">
          <Alert type="error">{error}</Alert>
        </div>
      )}
      <div className="w-[36rem] space-y-5 p-8">
        <div className="space-y-1">
          <SelectMenu<RuleTypeOption>
            label="Alert when"
            value={typeOption}
            items={RULE_TYPE_OPTIONS}
            onChange={(option) =>
              setFormValue((prev) =>
                withType(prev, option.id as PresenceRuleType)
              )
            }
            displayValue={(item) => item?.label ?? ''}
            id={(item) => item.id}
          />
          {typeOption && (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
              {typeOption.hint}
            </p>
          )}
        </div>

        <SelectMenu<ZoneOption>
          label="Zone"
          value={zoneOption}
          items={zoneOptions}
          onChange={(option) =>
            setFormValue((prev) => ({
              ...prev,
              zoneId: option.id,
              zoneName: option.zoneName,
            }))
          }
          displayValue={(item) => item?.name ?? ''}
          id={(item) => item.id}
          placeholder="Choose a zone"
        />

        {usesCount(formValue.type) && (
          <div className="space-y-1">
            <InputField
              type="number"
              label={
                formValue.type === 'max_visitors'
                  ? 'More than this many people'
                  : formValue.type === 'min_occupancy'
                  ? 'Fewer than this many people'
                  : 'At least this many people'
              }
              value={String(formValue.count)}
              onChange={numberUpdater('count')}
              min="1"
            />
            {countCaution && (
              <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                {countCaution}
              </p>
            )}
          </div>
        )}

        {usesMinutes(formValue.type) && (
          <div className="space-y-1">
            <InputField
              type="number"
              label={
                formValue.type === 'max_occupancy'
                  ? 'For at least this many minutes (0 = immediately)'
                  : 'For at least this many minutes'
              }
              value={String(formValue.thresholdMinutes)}
              onChange={numberUpdater('thresholdMinutes')}
              min="0"
            />
            {minutesCaution && (
              <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                {minutesCaution}
              </p>
            )}
          </div>
        )}

        {usesWindow(formValue.type) && (
          <InputField
            type="number"
            label="Within this many minutes (at most 60)"
            value={String(formValue.windowMinutes)}
            onChange={numberUpdater('windowMinutes')}
            min="1"
            max="60"
          />
        )}

        <Toggle
          label="Enabled"
          enabled={formValue.enabled}
          onChange={(enabled) => setFormValue((prev) => ({ ...prev, enabled }))}
        />

        <div className="border-onlight-line-2 dark:border-ondark-line-2 rounded-md border px-3 py-2">
          <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs uppercase tracking-wide">
            This rule
          </p>
          <p className="text-onlight-text-1 dark:text-ondark-text-1 text-sm">
            {problem ?? describeRule(formValue)}
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default AddEditPresenceRule;
